import { Component, type ErrorInfo, type ReactNode } from 'react';

import { RELOAD_MARK, isChunkFailure, shouldReload } from './ErrorBoundary.logic.js';
import { Button } from './ui/Button.js';

/**
 * The console's only backstop against a blank page.
 *
 * WHY THIS EXISTS. React 18 unmounts the ENTIRE root when a render throws and
 * nothing catches it. There is no partial degradation and no console message a
 * user will ever see: `#root` is emptied, the page keeps the `bg-tint` body
 * background from `index.css`, and that is the whole of it. Reported from the
 * field as "manager.fieldvision.id/overview is just a blank background" — which
 * is exactly what an uncaught render error looks like, and is indistinguishable
 * from a dead server unless you open the devtools console.
 *
 * The console shipped with no boundary anywhere, so every one of those errors
 * cost the whole application rather than the panel that threw.
 *
 * THE COMMONEST CAUSE IS NOT A BUG IN A COMPONENT — it is a `lazy()` import that
 * failed to fetch. `/overview` alone pulls two of them (the Recharts charts),
 * and a rejected dynamic import throws to the nearest boundary just like a
 * render error does. Two ordinary things make that fetch fail:
 *
 *   - a network blip while the chunk is in flight, and
 *   - a DEPLOYMENT. `/assets/*` is served `immutable` for a year while
 *     `index.html` is not, so a tab holding a cached document asks for hashed
 *     filenames the new container no longer has. Every one of them 404s.
 *
 * The second case is why this reloads instead of only apologising: the document
 * in memory is stale, and a reload is not a workaround for it — it is the fix.
 */

/**
 * Reloads at most once per cooldown, and reports whether it is doing so.
 *
 * DECLINING WHEN `sessionStorage` IS UNAVAILABLE IS DELIBERATE. Private windows
 * and blocked site data both throw here, and without somewhere to write the mark
 * there is no way to tell the first reload from the hundredth — a reload loop is
 * a worse failure than the static page this falls back to.
 *
 * The mark is written BEFORE the reload, which is the only ordering that works:
 * `location.reload()` does not return, so anything after it never runs.
 */
function reloadOnce(): boolean {
  const now = Date.now();
  try {
    if (!shouldReload(window.sessionStorage.getItem(RELOAD_MARK), now)) return false;
    window.sessionStorage.setItem(RELOAD_MARK, String(now));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

interface State {
  error: Error | null;
  /** True while a reload is already on its way — render nothing, not an error. */
  reloading: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, reloading: false };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The one record anybody gets. Kept even when we are about to reload, so
    // the cause survives in the console of a manager who reports the symptom.
    console.error('[fieldvision] render failed', error, info.componentStack);

    if (isChunkFailure(error) && reloadOnce()) this.setState({ reloading: true });
  }

  private readonly retry = (): void => {
    // A full reload rather than clearing `error`: if the document itself is the
    // stale part, re-rendering the same tree fetches the same missing chunk.
    window.location.reload();
  };

  override render(): ReactNode {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;

    // The reload is already committed. Anything drawn here would flash for the
    // instant before navigation and read as an error the user has to act on.
    if (reloading) return null;

    const stale = isChunkFailure(error);

    return (
      <div className="flex min-h-dvh items-center justify-center p-gutter">
        <div className="surface max-w-md p-6">
          <h1 className="text-lg">{stale ? 'Versi aplikasi berubah' : 'Terjadi kesalahan'}</h1>
          <p className="mt-2 text-sm text-muted">
            {stale
              ? 'Sebagian aplikasi gagal dimuat, biasanya karena ada pembaruan atau koneksi terputus sesaat. Muat ulang halaman untuk mengambil versi terbaru.'
              : 'Halaman ini tidak dapat ditampilkan. Muat ulang halaman; jika masih berlanjut, laporkan pesan di bawah ini.'}
          </p>
          {/*
            The message, not the stack. A manager cannot read a minified stack
            and will not paste one accurately, but they will read out a single
            line — and that line is what makes the difference between "the app is
            broken" and a reproducible report.
          */}
          {stale ? null : (
            <p className="mt-3 break-words rounded-lg bg-tint p-3 font-mono text-xs text-muted">
              {error.message || error.name}
            </p>
          )}
          <Button variant="primary" className="mt-4" onClick={this.retry}>
            Muat ulang
          </Button>
        </div>
      </div>
    );
  }
}
